"use strict";
/**
 * 域名绑定契约。
 *
 * 这份文件是 P0-04 PoC 的"判据来源"：状态机怎么走、哪一步会失败、失败原因怎么翻译成人话，
 * 全部在这里定义，DNS 校验服务只负责产生事实、不负责定义语义。这样前端文案、告警分级、
 * 客服话术能共用同一套枚举，不会出现"后台显示 error、前端显示未知错误"。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ORIGIN_PROBE_VALUE = exports.ORIGIN_PROBE_HEADER = exports.ORIGIN_PROBE_PATH = exports.DOMAIN_VERIFY_TTL_DAYS = exports.DOMAIN_FAIL_MESSAGES = exports.DOMAIN_FAIL_REASONS = exports.DOMAIN_TRANSITIONS = exports.CERT_STATUSES = exports.DOMAIN_STATUSES = void 0;
exports.isDomainReady = isDomainReady;
exports.canTransition = canTransition;
exports.assertTransition = assertTransition;
exports.retryPolicy = retryPolicy;
exports.normalizeHost = normalizeHost;
exports.isValidHost = isValidHost;
exports.verificationTxtName = verificationTxtName;
exports.looksLikeApex = looksLikeApex;
exports.dnsInstructions = dnsInstructions;
exports.createVerifyToken = createVerifyToken;
exports.DOMAIN_STATUSES = [
    'pending_dns', // 客户刚填域名，还没开始校验
    'verifying', // 正在校验 TXT / CNAME
    'active', // 校验通过 + 证书就绪，可访问
    'error', // 校验失败，等客户改 DNS 或重试
    'suspended', // 因欠费/违规暂停解析
    'releasing', // 解绑中（先吊销证书，再释放 host）
    'deleted', // 已释放，可被其他租户绑定
];
/**
 * 证书状态，与 `status` 完全独立。
 *
 * 实测数据（docs/ops/cloudflare-for-saas-开通指引.md 第 11 节）：主机名先 active，
 * 证书约 60 秒后才 active。所以"能不能访问"必须两个字段都为 active，
 * 只看 status 就会给客户一个会报证书错误的地址。
 */
exports.CERT_STATUSES = ['none', 'pending', 'active', 'failed', 'expired'];
function isDomainReady(status, certStatus) {
    return status === 'active' && certStatus === 'active';
}
/**
 * 允许的流转。任何不在表里的流转都视为 bug，服务层必须拒绝而不是静默接受。
 * 注意 active → verifying 是合法的：证书续期失败或客户改了 DNS，需要重新校验。
 *
 * verifying → releasing 也是合法的：证书可能已经签出来而主机名还在 pending，
 * 此时解绑必须先吊销证书。少了这条边就只能"直接 deleted"，而那意味着跳过吊销，
 * 客户域名上会留着一张仍然有效的证书。
 */
exports.DOMAIN_TRANSITIONS = {
    pending_dns: ['verifying', 'deleted'],
    verifying: ['active', 'error', 'pending_dns', 'releasing', 'deleted'],
    active: ['verifying', 'suspended', 'releasing'],
    error: ['verifying', 'pending_dns', 'deleted'],
    suspended: ['active', 'releasing'],
    releasing: ['deleted'],
    deleted: [],
};
function canTransition(from, to) {
    return exports.DOMAIN_TRANSITIONS[from].includes(to);
}
function assertTransition(from, to) {
    if (!canTransition(from, to)) {
        throw new Error(`illegal domain transition: ${from} -> ${to}`);
    }
}
/** 失败原因枚举。前端按 key 取文案，绝不把供应商原始错误直接透给客户。 */
exports.DOMAIN_FAIL_REASONS = [
    'txt_missing',
    'txt_mismatch',
    'cname_missing',
    'cname_mismatch',
    /**
     * 根域上有 A 记录、但地址不在 Cloudflare 边缘。
     *
     * 这是 2026-11-03 放开根域绑定后新增的一档：根域只能 CNAME 到平台，而根域 CNAME
     * 要求 DNS 服务商支持扁平化（Cloudflare 支持，阿里云 / DNSPod 不支持）。客户在
     * 不支持扁平化的服务商上，多半会被引导去填一条 A 记录 —— 那正好是我们必须拒绝的
     * 形状（流量绕过 CF 直连源站，源站只有 *.icetus.net 证书，浏览器直接报不安全）。
     * 只有"根域 + 有地址 + 地址不在 CF"才落到这里，子域仍归 cname_missing。
     */
    'apex_needs_flattening',
    'nxdomain',
    'served_by_other_host',
    'cert_pending',
    'cert_rate_limited',
    'caa_blocked',
    'host_conflict',
    'unknown',
];
exports.DOMAIN_FAIL_MESSAGES = {
    txt_missing: {
        zh: '没有查到验证用的 TXT 记录，可能还没生效或填错了主机记录。',
        en: 'No TXT record found. It may not have propagated yet, or the host was entered incorrectly.',
        selfServiceable: true,
    },
    txt_mismatch: {
        // 补上"刚改过就等一会儿"这句：TXT 记录改动后，客户自己那侧的 DNS 服务商
        // 会按 TTL 缓存旧值，此时值确实对、我们也没查错，干等是最省事的解法。
        // 平台侧的本机缓存污染已在 2026-10-30 修掉（见 dns.ts），不会再出现
        // "值本来就是对的、却永远判不匹配"那种无解状态。
        zh: 'TXT 记录存在但内容不匹配，请对照页面上的完整值逐字核对；如果刚改过，DNS 缓存需要几分钟才刷新。',
        en: 'A TXT record exists but the value does not match. Compare it character by character with the value shown; a recent change can take a few minutes to propagate.',
        selfServiceable: true,
    },
    cname_missing: {
        zh: '没有查到 CNAME 记录，请检查是否添加到了正确的域名下。',
        en: 'No CNAME record found. Check that it was added under the correct domain.',
        selfServiceable: true,
    },
    apex_needs_flattening: {
        // 不能并进 cname_missing：这类客户不是"忘了加"，而是加不出来 —— 服务商的根域
        // 记录类型里根本没有 CNAME。让他"再检查一遍"只会原地打转，必须直接给替代方案。
        zh: '根域只能用 CNAME 指向平台，而根域 CNAME 需要 DNS 服务商支持「CNAME 扁平化」（Cloudflare 支持，阿里云、DNSPod 等不支持）。检测到根域上是一条 A 记录：如果你用的服务商不支持，请改为绑定 www 子域名作为主站，再把根域 301 跳到 www。',
        en: 'An apex domain can only point here through a CNAME, which requires your DNS provider to support CNAME flattening (Cloudflare does; Alibaba Cloud and DNSPod do not). We found an A record on the apex: if your provider does not support flattening, bind the www subdomain instead and redirect the apex to www.',
        selfServiceable: true,
    },
    cname_mismatch: {
        zh: 'CNAME 指向了其他地址，请改为平台提供的目标地址。',
        en: 'The CNAME points elsewhere. Change it to the target address provided by the platform.',
        selfServiceable: true,
    },
    nxdomain: {
        zh: '该域名当前无法解析，请确认域名已注册且 DNS 服务正常。',
        en: 'The domain does not resolve. Confirm it is registered and its DNS service is working.',
        selfServiceable: true,
    },
    served_by_other_host: {
        // 由激活前的端到端探针产生（见 ORIGIN_PROBE_*）。典型成因：客户把 CNAME 指到了
        // **自己的另一个 Cloudflare 站点**，地址同样落在 CF 网段，DNS 层面看不出区别，
        // 但流量到的是别人家的源站。不说清"响应来自另一个网站"，客户会一直以为是我们这边有 bug。
        zh: '域名已经指向 Cloudflare，但请求没有到达本站（响应来自另一个网站）。请检查 CNAME 记录是否指向平台提供的地址，而不是你自己的其他 Cloudflare 站点。',
        en: 'The domain points at Cloudflare, but requests are not reaching this site — the response comes from a different website. Check that the CNAME targets the address shown here, not another Cloudflare site of yours.',
        selfServiceable: true,
    },
    cert_pending: {
        zh: '证书正在签发，通常几分钟内完成，无需操作。',
        en: 'The certificate is being issued. This usually takes a few minutes; no action needed.',
        selfServiceable: true,
    },
    cert_rate_limited: {
        zh: '证书申请过于频繁被限流，系统会在稍后自动重试。',
        en: 'Certificate requests are rate limited. The system will retry automatically.',
        selfServiceable: false,
    },
    caa_blocked: {
        zh: '你的域名配置了 CAA 记录，只允许特定机构签发证书，需要加上我们使用的签发机构。',
        en: 'Your domain has a CAA record that only allows specific CAs. Add our certificate authority to it.',
        selfServiceable: false,
    },
    host_conflict: {
        zh: '该域名已被其他账户占用，请联系客服核实归属。',
        en: 'This domain is already claimed by another account. Contact support to verify ownership.',
        selfServiceable: false,
    },
    unknown: {
        zh: '校验遇到未知问题，已记录，请稍后重试或联系客服。',
        en: 'An unexpected error occurred. It has been logged; retry later or contact support.',
        selfServiceable: false,
    },
};
/** 重试策略：可自修的错误让客户改 DNS；不可自修的走后台人工，避免无效轮询打爆供应商接口 */
function retryPolicy(reason) {
    const msg = exports.DOMAIN_FAIL_MESSAGES[reason];
    if (reason === 'cert_pending')
        return { autoRetry: true, nextCheckSeconds: 30 };
    if (reason === 'cert_rate_limited')
        return { autoRetry: true, nextCheckSeconds: 3600 };
    if (msg.selfServiceable)
        return { autoRetry: false, nextCheckSeconds: 0 };
    return { autoRetry: false, nextCheckSeconds: 0 };
}
/**
 * 主机名规范化。客户会粘贴 `https://Www.Abc.com/` 这种东西，必须先收敛再入库，
 * 否则同一域名会以多种形态占多行，唯一索引形同虚设。
 */
function normalizeHost(input) {
    let host = input.trim().toLowerCase();
    host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, ''); // 去 scheme
    host = host.split('/')[0] ?? ''; // 去路径
    host = host.split('?')[0] ?? '';
    host = host.split('#')[0] ?? '';
    host = host.split('@').pop() ?? ''; // 去 userinfo
    host = host.replace(/:\d+$/, ''); // 去端口
    host = host.replace(/\.+$/, ''); // 去尾部点
    return host;
}
const LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
function isValidHost(host) {
    if (!host || host.length > 253)
        return false;
    if (!host.includes('.'))
        return false;
    const labels = host.split('.');
    if (labels.length < 2)
        return false;
    const tld = labels[labels.length - 1];
    if (!tld || tld.length < 2 || /^\d+$/.test(tld))
        return false;
    return labels.every((l) => LABEL_RE.test(l));
}
/**
 * 校验用 TXT 记录的主机名。
 *
 * 为什么不用客户主域本身：客户主域上通常已经有 SPF / 域名验证 / 各种第三方 TXT，
 * 混在一起既容易冲突，也让客户不知道该改哪条。用一个专属子域 `_icetus-verify.<host>`
 * 语义明确、互不干扰，解绑时只删这一条。
 */
function verificationTxtName(host) {
    return `_icetus-verify.${normalizeHost(host)}`;
}
/**
 * 两级公共后缀。只为把 `abc.com.cn` 正确判成根域——
 * 外贸客户里 .com.cn / com.hk / co.uk 很常见，光数 label 会漏。
 *
 * 不引公共后缀列表（psl）的原因：那是几万行的数据文件，还要随版本更新；
 * 这里判错的代价只是"绑定被拦下让客户改 www"，不是安全事故。
 */
const COMPOUND_SUFFIXES = new Set([
    'com.cn', 'net.cn', 'org.cn', 'gov.cn', 'edu.cn', 'com.hk', 'com.tw', 'com.mo',
    'co.uk', 'org.uk', 'me.uk', 'co.jp', 'ne.jp', 'co.kr', 'com.sg', 'com.my',
    'com.ph', 'com.vn', 'co.th', 'com.au', 'net.au', 'co.nz', 'co.in', 'com.br',
    'com.mx', 'com.tr', 'com.sa', 'com.ae', 'co.za', 'com.ar', 'com.co',
]);
/**
 * 判断是不是根域（apex）。
 *
 * 只做判断，不做放行依据的最后裁决：真正的约束是"根域不能有 CNAME"，
 * 由调用方决定是拦下来还是改引导方式。
 */
function looksLikeApex(host) {
    const labels = normalizeHost(host).split('.');
    if (labels.length <= 2)
        return true;
    if (labels.length === 3)
        return COMPOUND_SUFFIXES.has(labels.slice(1).join('.'));
    return false;
}
/**
 * 生成 DNS 配置指引。
 *
 * 子域与根域都只给一条 CNAME，差别在**主机记录**和那句提示：
 *   - 子域：主机记录填前缀（`shop`）；
 *   - 根域：主机记录填 `@`。存不存得下去取决于 DNS 服务商是否支持 CNAME 扁平化
 *     （Cloudflare 的 zone 直接支持；阿里云、DNSPod 的根域记录类型里没有 CNAME）。
 *
 * 2026-11-03 之前根域在这里返回空数组、产品层直接拒绝绑定，理由是"根域不能有 CNAME，
 * 放行必然卡在 cname_missing"。实测推翻了这个前提：`icetus.cn`（独立 CF zone）
 * 根域 CNAME 到 cname.icetus.net、开着橙云 → CF 30 秒签出证书，请求穿过 fallback
 * origin 到达源站。根域和 www 走的是同一条路，不需要"帮客户把根域 301 到 www"
 * 这种我们做不到的承诺。
 */
function dnsInstructions(host, opts = { cnameTarget: 'cname.icetus.example' }) {
    const { cnameTarget } = opts;
    const isApex = looksLikeApex(host);
    const label = host.split('.')[0] ?? '@';
    return [
        {
            type: 'CNAME',
            name: isApex ? '@' : label,
            value: cnameTarget,
            note: isApex
                ? {
                    zh: `在根域添加 CNAME：主机记录填 @，记录值填 ${cnameTarget}。根域 CNAME 要求 DNS 服务商支持「扁平化」（Cloudflare 可以；阿里云、DNSPod 的根域不支持，遇到这种情况请改用 www 绑定主站，再把根域 301 到 www）。`,
                    en: `Add a CNAME at the root: host "@", value ${cnameTarget}. An apex CNAME requires CNAME flattening support (Cloudflare supports it; Alibaba Cloud and DNSPod do not — if so, bind www instead and redirect the apex to www).`,
                }
                : {
                    zh: `在 DNS 后台添加 CNAME，主机记录填 ${label}，记录值填 ${cnameTarget}`,
                    en: `Add a CNAME with host "${label}" pointing to ${cnameTarget}`,
                },
        },
    ];
}
/** 验证令牌有效期（天）。与 0004 迁移里给存量数据回填的 7 天保持一致 */
exports.DOMAIN_VERIFY_TTL_DAYS = 7;
/**
 * 生成 TXT 验证令牌。
 *
 * 必须用密码学随机源：这个值等价于"域名所有权凭证"，可预测就等于不用验证。
 * 用 `globalThis.crypto` 而不是 `node:crypto`，因为 contracts 也会被打进控制台前端包。
 */
function createVerifyToken() {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}
/**
 * 激活前探针：确认这个域名的流量真的落在**我们的**源站上。
 *
 * 为什么必须有：TXT 只证明"域名归你"，CNAME/A 只证明"流量进了 Cloudflare"。
 * 客户把 CNAME 指向**自己的另一个 CF 站点**时，两件事全都成立 —— 我们照样签出证书、
 * 把域名标成 active，而访客看到的是别人家的页面。DNS 层面没有任何办法区分，
 * 只有发一个请求看谁来应答。
 *
 * 探针打的是站点进程上的固定端点，它不依赖这个域名是否已经绑定（也就是激活前后
 * 都能应答），因此可以放在"证书已签好、还没 activate"那一刻。
 * 三个值在两端共用：站点侧写头，平台侧读头。
 */
exports.ORIGIN_PROBE_PATH = '/api/origin';
exports.ORIGIN_PROBE_HEADER = 'x-icetus-origin';
exports.ORIGIN_PROBE_VALUE = 'icetus';
//# sourceMappingURL=domain.js.map